// Phase C.4a — pure aggregation/derivation helpers for the Division Hub
// (the one shared presentation the private Division management surface
// and the public Division page both render — see components/programs/
// DivisionHub.js). Not a new event-storage model: everything here only
// reshapes/derives from what already exists — each Division Team's own
// team_events (external games/practice/other) and team_matchups
// (internal Division games, 042). Kept as plain functions (no React) so
// this logic is unit-testable without a brittle component test — see
// divisionSchedule.test.js.

import { deriveTeamRecord } from './gameResult';
import { isEventUpcoming } from './teamSchedule';

/** @param {Array<{team: object, events: array, matchups: array}>} perTeam
 *  one entry per Division Team, each already loaded via listTeamEvents()/
 *  listTeamMatchups() (or the public equivalents) for that Team.
 *
 *  An internal matchup between two Teams in the SAME Division is present
 *  in BOTH participants' own matchup list (each already perspective-
 *  normalized to that Team's own point of view). Phase C.5a: the SAME
 *  is now also true of a generalized event — an entire-Division or
 *  specific-Teams event (043) comes back from EVERY participating
 *  Team's own listTeamEvents() call, not just one. Aggregating naively
 *  across every Team in the Division would show either kind of row
 *  once per participant; this de-duplicates ALL rows (event and
 *  matchup alike) by their own `id`, keeping only the first occurrence
 *  encountered. Every matchup row is also stamped `event_type: 'game'`
 *  (matching TeamSchedulePage.js's own established convention) — a
 *  matchup is always a Game, never Practice/Other, but the raw RPC row
 *  doesn't carry that field itself.
 *
 *  `ownerTeamId`/`ownerTeamName` are only ever stamped for a row that
 *  genuinely belongs to ONE Team — a plain team-scoped event (`scope_
 *  type` absent/'team'), or a matchup (always exactly one two-Team
 *  relationship, perspective-normalized to whichever Team's own fetch
 *  first produced the surviving row). An entire-Division or specific-
 *  Teams event has no single owning Team by design — see
 *  describeEventScope() below for how callers should label it instead;
 *  stamping an arbitrary "whichever Team was iterated first" owner here
 *  would misrepresent it as Team-owned, exactly the truthfulness
 *  problem C.5a's own generalized scope model exists to fix.
 *
 *  @returns {Array} one row per real competition/event, chronological
 *  (event_date then start_time, both ascending), each tagged `kind`
 *  ('event' | 'matchup'). */
export function mergeDivisionSchedule(perTeam) {
  const seenIds = new Set();
  const rows = [];

  for (const entry of perTeam || []) {
    const { team, events, matchups } = entry;
    for (const e of events || []) {
      if (seenIds.has(e.id)) continue;
      seenIds.add(e.id);
      const isTeamScoped = !e.scope_type || e.scope_type === 'team';
      rows.push({
        ...e, kind: 'event',
        ownerTeamId: isTeamScoped ? team.id : null,
        ownerTeamName: isTeamScoped ? team.team_name : null,
      });
    }
    for (const m of matchups || []) {
      if (seenIds.has(m.id)) continue;
      seenIds.add(m.id);
      rows.push({ ...m, event_type: 'game', kind: 'matchup', ownerTeamId: team.id, ownerTeamName: team.team_name });
    }
  }

  rows.sort((a, b) => {
    const dateCompare = String(a.event_date || '').localeCompare(String(b.event_date || ''));
    if (dateCompare !== 0) return dateCompare;
    return String(a.start_time || '').localeCompare(String(b.start_time || ''));
  });

  return rows;
}

/** Phase C.5e — TEAM-FOCUSED SCHEDULE. Filters an already-merged `rows`
 * list (mergeDivisionSchedule()'s own output) down to only the rows
 * genuinely relevant to one Team — its own Games/Practice/Other, any
 * matchup it participates in, any entire-Division event (applies to
 * every Team by definition), and any specific-Teams event whose
 * membership includes it. An unrelated sibling Team's own Team-scoped
 * Practice/Other is excluded — that's the exact "unrelated clutter"
 * TeamSchedulePage.js must not show. Used for Schedule/Needs-Result/
 * Up-Next/Results on a Team-focused view; the TEAMS/records panel
 * deliberately does NOT use this — it stays Division-wide for
 * competitive context, per this phase's own explicit product rule. */
export function filterRowsForTeam(rows, teamId) {
  return (rows || []).filter((row) => {
    if (row.kind === 'matchup') {
      return row.ownerTeamId === teamId || row.opponent_team_id === teamId;
    }
    if (!row.scope_type || row.scope_type === 'team') return row.ownerTeamId === teamId;
    if (row.scope_type === 'entire_division') return true;
    if (row.scope_type === 'specific_teams') return (row.scope_team_ids || []).includes(teamId);
    return false;
  });
}

/** A truthful display label for a generalized event's scope — never a
 * pretend single owner for an entire-Division/specific-Teams row (see
 * mergeDivisionSchedule()'s own header for why). `teamNamesById` is an
 * optional `{ [teamId]: team_name }` lookup — when available (the
 * Division Hub always has the full roster on hand), a specific-Teams
 * event names its actual participants; when it isn't (an individual
 * Team's own Schedule page, which doesn't otherwise need the rest of
 * the Division's roster), it falls back to a generic but still honest
 * "Specific Teams" rather than fabricating names. */
export function describeEventScope(row, teamNamesById) {
  if (row.scope_type === 'entire_division') return 'Entire Division';
  if (row.scope_type === 'specific_teams') {
    const names = (row.scope_team_ids || [])
      .map(id => (teamNamesById || {})[id])
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'Specific Teams';
  }
  return row.ownerTeamName || '';
}

/** Converts one row of list_public_division_games()/listPublicDivisionGames
 * (042) — a raw team_a/team_b pair, not oriented to either side — into
 * the SAME per-team-perspective shape list_team_matchups()/
 * list_public_team_matchups() already produce server-side
 * (opponent_name/opponent_team_id/home_away/team_score/opponent_score).
 * This lets the PUBLIC Division Hub build a `perTeam`-shaped structure
 * from ONE bulk call (rather than one public RPC per Team) and still
 * reuse mergeDivisionSchedule()'s own dedup/sort untouched — calling
 * this once per Team for every division-wide matchup row naturally
 * dedupes back down to one row, exactly like the authenticated path.
 * Mirrors the identical CASE logic already used by 042's own SQL
 * functions — not a new business rule, just the same transform done
 * client-side for a bulk-fetched source. */
export function perspectiveFromDivisionGame(game, teamId) {
  const isTeamA = game.team_a_id === teamId;
  const home_away = game.site === 'neutral' ? 'neutral' : ((isTeamA ? game.site === 'team_a_home' : game.site === 'team_b_home') ? 'home' : 'away');
  return {
    id: game.id,
    event_type: 'game',
    status: game.status,
    event_date: game.event_date,
    start_time: game.start_time,
    opponent_team_id: isTeamA ? game.team_b_id : game.team_a_id,
    opponent_name: isTeamA ? game.team_b_name : game.team_a_name,
    home_away,
    team_score: isTeamA ? game.team_a_score : game.team_b_score,
    opponent_score: isTeamA ? game.team_b_score : game.team_a_score,
  };
}

/** Phase C.5e (extracted from PublicDivisionPage.js's own previously-
 * private helper of the same shape, so a second caller — TeamSchedulePage
 * .js's own public-safe records fallback for a viewer with no Program-
 * level authority — never has to reimplement it) — builds a `perTeam`-
 * shaped array (exactly what mergeDivisionSchedule()/
 * deriveDivisionTeamRecordsFromRows() already expect) from bulk
 * PUBLIC-safe data: one list_public_division_games() call (`divisionGames`,
 * every internal matchup in the Division) plus a `{ [teamId]: rows }` map
 * of each Team's own list_public_team_games() result (`externalGamesByTeamId`
 * — an unpublished/no-data Team simply contributes an empty array, never
 * an error). `event_type: 'game'` is stamped onto each external game here
 * — list_public_team_games() (041) doesn't return that column at all
 * (every row it returns is a Game by construction, so the RPC never
 * needed to say so), but mergeDivisionSchedule()'s own dedup/derive
 * pipeline downstream requires it on every row. This ALSO fixes a
 * latent gap in the original, non-exported version of this function:
 * without this stamp, an external Game surfaced only through
 * list_public_team_games() would have silently failed every
 * `event_type === 'game'` check downstream (Upcoming/Results/Records) —
 * internal matchups were never affected (mergeDivisionSchedule() already
 * stamps event_type itself for those), so this was easy to miss. */
export function buildPublicPerTeam(teams, divisionGames, externalGamesByTeamId) {
  return (teams || []).map((team) => {
    const matchups = (divisionGames || [])
      .filter(g => g.team_a_id === team.id || g.team_b_id === team.id)
      .map(g => perspectiveFromDivisionGame(g, team.id));
    const events = ((externalGamesByTeamId || {})[team.id] || []).map(g => ({ ...g, event_type: 'game' }));
    return { team, events, matchups };
  });
}

/** Phase C.5e follow-up (browser-found) — resolves the complete OTHER-
 * Division-Team membership list (every Division Team except the focused
 * one) for the Team-focused TEAMS/records panel, from a raw source
 * appropriate to the viewer's authority level. Extracted as a pure
 * function specifically so this decision — the exact one that regressed
 * in the browser (Green/Yellow, then even Black, silently missing from
 * Blue's own TEAMS panel) — is unit-testable with realistic RPC response
 * shapes, independent of TeamSchedulePage.js's own react-router-dom
 * dependency (which blocks RTL testing there entirely).
 *
 * BROWSER-FOUND BUG — list_division_sibling_teams() (042) was originally
 * used for this. Read literally, its SQL returns every OTHER active Team
 * in the caller's Division regardless of authority tier — but its real
 * behavior for a plain Team Head Coach proved unreliable in the browser
 * (an incomplete roster for one Team, an entirely empty one for another),
 * for reasons not reproducible from the SQL text alone. Rather than layer
 * another speculative fallback onto an unreliable source, membership is
 * now resolved from whichever EXISTING source is actually authoritative
 * for this viewer:
 *
 *  - Program-authorized viewer (`teamAuthority` true): `allProgramTeams`
 *    — a raw, RLS-authorized listProgramSeasonTeams() read of the full
 *    Program roster, filtered to this one Division. This is the EXACT
 *    same source DivisionSchedulePage.js's own Full Division Hub already
 *    uses for membership (proven correct/never regressed) — not a new or
 *    parallel mechanism.
 *  - Everyone else: `publicDivisionTeams` — list_public_division_teams()
 *    (038), the same existing public-safe Division roster
 *    PublicDivisionPage.js already uses. No new RPC, no broadened RLS, no
 *    private sibling Practice/Other/Notes ever touched. Honest degrade:
 *    if the Program itself isn't publicly visible, this returns empty —
 *    exactly what an anonymous visitor would also see, never less than
 *    that, never a privacy hole.
 *
 * Returns a plain `{ id, team_name }` array, excluding `excludeTeamId`
 * when one is given — the shape `mergeDivisionSchedule()`/
 * `buildPublicPerTeam()` already expect as one `perTeam` entry's `team`.
 *
 * Phase C.5e.1 (schedule consolidation) — `excludeTeamId` is OPTIONAL:
 * Team-focused mode passes the focused Team's own id (excluded because
 * its own entry is built separately, from its own private fetch);
 * Division mode passes nothing at all, since there is no focused Team to
 * exclude — every Division Team is real membership there. The SAME
 * function serves both of the one canonical schedule controller's modes
 * (scheduleController.js) — no parallel "division membership" logic. */
export function resolveDivisionMembers({ excludeTeamId, divisionId, teamAuthority, allProgramTeams, publicDivisionTeams }) {
  const source = teamAuthority
    ? (allProgramTeams || []).filter(t => t.division_id === divisionId)
    : (publicDivisionTeams || []);
  return source
    .filter(t => t.id !== excludeTeamId)
    .map(t => ({ id: t.id, team_name: t.team_name }));
}

/** Upcoming Games — the highlighted subset near the top of the Hub.
 * Games only (Practice/Other never appear here, regardless of viewer):
 * not yet played (status scheduled or postponed) and still upcoming per
 * isEventUpcoming() (C.5e — same-day, end_time/start_time-aware; a game
 * that ended earlier today no longer qualifies), capped at `limit`.
 * Relies on `rows` already being sorted ascending by
 * mergeDivisionSchedule(). */
export function deriveUpcomingGames(rows, { today, nowTimeStr, limit = 5 } = {}) {
  return (rows || [])
    .filter(r => r.event_type === 'game' && (r.status === 'scheduled' || r.status === 'postponed') && isEventUpcoming(r, { todayStr: today, nowTimeStr }))
    .slice(0, limit);
}

/** NEEDS RESULT (C.5d lifecycle fix) — closes the exact discoverability
 * gap the lifecycle audit found: a Game whose date has passed but is
 * still 'scheduled' (nobody entered a result) matches NONE of Upcoming
 * (event_date >= today), Scheduled (event_date >= today), or Results
 * (status = 'completed') — it was previously invisible on this Hub
 * entirely, even though the row was fully intact. Games only (never
 * Practice/Other — this is specifically a competitive-result concern,
 * not a general "past events" section); `status = 'scheduled'` only —
 * deliberately excludes 'postponed' (a postponed Game may never have
 * happened on its original date at all, so it stays POSTPONED, not
 * "needs a result"), and obviously excludes 'completed'/'cancelled'. */
export function deriveNeedsResult(rows, { today } = {}) {
  return (rows || []).filter(r => r.event_type === 'game' && r.status === 'scheduled' && r.event_date < today);
}

/** Results — a chronological ledger of completed Games only (Practice/
 * Other excluded, same as Upcoming), grouped by actual event_date, most
 * recent date group first. Each group's own rows are ordered by
 * start_time ascending. */
export function deriveResultsByDate(rows) {
  const completed = (rows || []).filter(r => r.event_type === 'game' && r.status === 'completed');
  const byDate = new Map();
  for (const r of completed) {
    if (!byDate.has(r.event_date)) byDate.set(r.event_date, []);
    byDate.get(r.event_date).push(r);
  }
  return [...byDate.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, games]) => ({
      date,
      games: [...games].sort((x, y) => String(x.start_time || '').localeCompare(String(y.start_time || ''))),
    }));
}

/** Scheduled — the forward-looking calendar: still upcoming per
 * isEventUpcoming() (C.5e — same-day, end_time/start_time-aware) and not
 * yet completed (a completed item belongs in Results instead). Includes
 * Practice/Other alongside Games for callers that pass them in (the
 * authenticated Hub); a caller that only ever passes Game rows (the
 * public Hub, which never receives Practice/Other from any public-safe
 * source) naturally gets a Games-only Scheduled section with no extra
 * filtering needed here. Cancelled/postponed rows are NOT excluded —
 * they stay visible, clearly tagged, rather than silently disappearing.
 * Grouped by calendar date ascending, then start_time ascending within
 * a date. */
export function deriveScheduledByDate(rows, { today, nowTimeStr } = {}) {
  const relevant = (rows || []).filter(r => r.status !== 'completed' && isEventUpcoming(r, { todayStr: today, nowTimeStr }));
  const byDate = new Map();
  for (const r of relevant) {
    if (!byDate.has(r.event_date)) byDate.set(r.event_date, []);
    byDate.get(r.event_date).push(r);
  }
  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, events]) => ({
      date,
      events: [...events].sort((x, y) => String(x.start_time || '').localeCompare(String(y.start_time || ''))),
    }));
}

/** TEAMS column — one derived SEASON RECORD per Division Team.
 *
 * CANONICAL RULE (product decision, superseding the prior "Division
 * standings, internal-only" behavior below): a Team's record is EVERY
 * completed Game belonging to that Team during the Season, regardless
 * of who the opponent is — another FFN Team in the same Division,
 * another FFN Team elsewhere in the Program, or an external/non-FFN
 * opponent entirely. TEAM RECORD and DIVISION STANDINGS are two
 * DIFFERENT concepts: this function computes the former; the latter
 * (competition-specific, internal-Division-only rules, with
 * tiebreakers) is an explicitly separate, not-yet-built future concept
 * and must not be confused with what this panel shows. Nothing in this
 * codebase currently computes real standings — do not call this
 * function or the TEAMS panel "standings" in comments or product copy.
 *
 * BROWSER-FOUND BUG FIXED (C.5e) — a completed "Black 16–12 Blue"
 * matchup showed Black 1-0 correctly but Blue as "—" (no record at
 * all). The previous version of this function derived each Team's
 * record from ITS OWN independently-fetched perspective copy of the
 * matchup (`perTeam[i].matchups`, one perspective-normalized row per
 * Team, fetched separately). That's fragile by construction: Black's
 * and Blue's copies are supposed to be mirror images of the same
 * underlying row, but nothing guarantees both fetches succeed
 * symmetrically — if either side's own fetch is empty, delayed, or
 * excluded for any reason, that ONE Team's record silently goes blank
 * while the other Team's (whose own fetch succeeded) still shows
 * correctly. That's exactly the asymmetric failure observed.
 *
 * The fix removes the fragility instead of papering over the specific
 * Team it happened to hit: records are derived from the SAME single
 * authoritative, already-deduplicated `rows` list mergeDivisionSchedule()
 * produces for Results/Upcoming/Scheduled — the one row a completed
 * matchup keeps after dedup. For that one row, BOTH participating
 * Teams' records are credited directly from it: the owning Team from
 * its own team_score/opponent_score exactly as stored, and the OTHER
 * Team (`opponent_team_id`) from the arithmetically flipped
 * perspective — never from a second, independently-fetched copy. A
 * completed matchup now only requires ONE side's data to have made it
 * into `rows` for BOTH Teams' records to derive correctly.
 *
 * REVISED (product decision — external games now count) — a Team's own
 * external (Outside Program, non-FFN opponent) Game is a `kind: 'event'`
 * row with `event_type === 'game'` (a Team-vs-Team Game between two real
 * FFN Teams is NEVER stored this way — GameFormDialogs.js's own
 * `isInternalGame` branch guarantees it becomes a `team_matchups` row
 * instead, handled by the block above). Such a row credits ONLY its one
 * owning Team (`ownerTeamId` — always set for a Game row, which the
 * database's own chk_events_game_scope_is_team CHECK constrains to
 * scope_type='team') from its own team_score/opponent_score exactly as
 * stored. There is no second FFN Team to credit — `opponent_team_id` is
 * null for an external Game by construction (chk_events_scope_shape),
 * so no fabricated record is ever created for the external opponent.
 * Practice/Other still never affect any Team's record — this branch is
 * additionally gated on `event_type === 'game'`, so only Games (internal
 * or external) ever reach either credit path. */
export function deriveDivisionTeamRecordsFromRows(perTeam, rows) {
  const gamesByTeamId = new Map((perTeam || []).map(({ team }) => [team.id, []]));
  for (const row of rows || []) {
    if (row.kind === 'matchup') {
      if (gamesByTeamId.has(row.ownerTeamId)) {
        gamesByTeamId.get(row.ownerTeamId).push({ status: row.status, team_score: row.team_score, opponent_score: row.opponent_score });
      }
      if (row.opponent_team_id && gamesByTeamId.has(row.opponent_team_id)) {
        gamesByTeamId.get(row.opponent_team_id).push({ status: row.status, team_score: row.opponent_score, opponent_score: row.team_score });
      }
    } else if (row.kind === 'event' && row.event_type === 'game' && gamesByTeamId.has(row.ownerTeamId)) {
      gamesByTeamId.get(row.ownerTeamId).push({ status: row.status, team_score: row.team_score, opponent_score: row.opponent_score });
    }
  }
  return (perTeam || []).map(({ team }) => ({ team, record: deriveTeamRecord(gamesByTeamId.get(team.id) || []) }));
}

/** A stable, defensible display ordering for the TEAMS column — NOT an
 * official ranking (no tiebreaker rules, no points-for/against exist
 * yet; see this phase's own Week/Round+Bye architecture note). Most wins
 * first, fewest losses next, then alphabetical for full stability. */
export function sortTeamRecordsForDisplay(teamRecords) {
  return [...(teamRecords || [])].sort((a, b) => {
    if (b.record.wins !== a.record.wins) return b.record.wins - a.record.wins;
    if (a.record.losses !== b.record.losses) return a.record.losses - b.record.losses;
    return String(a.team.team_name || '').localeCompare(String(b.team.team_name || ''));
  });
}
